import { useState, type FormEvent } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { api, ApiError } from "@/lib/api";
import { oneOf } from "@/lib/searchParams";
import { Panel } from "@/components/ui/Panel";
import { DataTable } from "@/components/ui/DataTable";
import { EmptyState } from "@/components/ui/EmptyState";
import { useToast } from "@/features/toast/ToastProvider";
import { useConfirm } from "@/features/confirm/ConfirmProvider";
import { ImpersonateButton } from "@/features/auth/ImpersonateButton";
import { formatDate, formatPercent } from "@bookboost/shared/format";

interface UserRow {
  id: string;
  email: string;
  full_name: string | null;
  role: "admin" | "rep" | "finance";
  hubspot_owner_id: string | null;
  salary_currency: string | null;
  active: boolean;
  created_at: string;
}

interface StructureRow {
  user_id: string;
  structure_type: "flat" | "split_equity";
  base_commission_rate: number;
  equity_split_rate: number | null;
  equity_annual_cap: number | null;
  cap_currency: string | null;
}

const repsSearch = z.object({
  q: z.string().optional(),
  role: z.enum(["admin", "rep", "finance"]).optional(),
  active: z.enum(["1", "0"]).optional(),
});

export const Route = createFileRoute("/admin/reps")({
  validateSearch: repsSearch,
  component: RepsPage,
});

function RepsPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "reps", search],
    queryFn: () =>
      api.get<{ users: UserRow[]; structures: StructureRow[] }>("/api/admin/reps", {
        searchParams: { q: search.q, role: search.role, active: search.active },
      }),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["admin", "reps"] });

  const invite = useMutation({
    mutationFn: (body: { email: string; full_name: string | null; role: string; hubspot_owner_id: string | null; salary_currency: string | null }) =>
      api.post("/api/admin/reps", body),
    onSuccess: (_, vars) => {
      toast.ok(`Invited ${vars.email} as ${vars.role}.`);
      invalidate();
    },
    onError: (err) => toast.err(err instanceof ApiError ? err.message : "Couldn't invite."),
  });

  const update = useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Partial<UserRow>) =>
      api.patch(`/api/admin/reps/${id}`, body),
    onSuccess: () => {
      toast.ok("Updated.");
      invalidate();
    },
    onError: (err) => toast.err(err instanceof ApiError ? err.message : "Couldn't update."),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/api/admin/reps/${id}`),
    onSuccess: () => {
      toast.ok("Deleted.");
      invalidate();
    },
    onError: (err) => toast.err(err instanceof ApiError ? err.message : "Couldn't delete."),
  });

  const anonymize = useMutation({
    mutationFn: (id: string) => api.post<{ ok: true; oldEmail: string }>(`/api/admin/reps/${id}/anonymize`),
    onSuccess: (res) => {
      toast.ok(`Anonymised ${res.oldEmail}. Commission history preserved.`);
      invalidate();
    },
    onError: (err) => toast.err(err instanceof ApiError ? err.message : "Couldn't anonymise."),
  });

  const syncOwners = useMutation({
    mutationFn: (body: { default_role: "admin" | "rep" | "finance"; mark_active: boolean }) =>
      api.post<{ created: number; mappedExisting: number; skipped: number; dealsAttributed: number; errors: string[] }>(
        "/api/admin/reps/sync-owners",
        body,
      ),
    onSuccess: (res) => {
      const msg = `Created ${res.created}, mapped ${res.mappedExisting} existing, skipped ${res.skipped}. Attributed ${res.dealsAttributed} deal${res.dealsAttributed === 1 ? "" : "s"}.`;
      if (res.errors.length > 0) toast.warn(`${msg} ${res.errors.length} error(s) — check logs.`, 9000);
      else toast.ok(msg, 7000);
      invalidate();
    },
    onError: (err) => toast.err(err instanceof ApiError ? err.message : "Couldn't sync owners."),
  });

  if (isLoading) return <p className="lede">Loading reps…</p>;
  if (error || !data) {
    return (
      <Panel variant="err">
        <h2>Reps unavailable</h2>
        <p>{error instanceof Error ? error.message : "Failed to load."}</p>
      </Panel>
    );
  }

  const structureByUser = new Map(data.structures.map((s) => [s.user_id, s]));

  return (
    <>
      <h1>Reps</h1>

      <Panel title="Sync sales team from HubSpot">
        <p className="muted">
          Pulls every HubSpot owner and creates a matching profile row here, pre-mapped to their HubSpot Owner ID.
          Existing users (matched by email or owner ID) are left alone apart from filling in a missing HubSpot Owner ID.
          Safe to re-run. <strong>No invitation emails are sent</strong> — emails are marked pre-confirmed.
        </p>
        <SyncOwnersForm onSubmit={(v) => syncOwners.mutate(v)} disabled={syncOwners.isPending} />
      </Panel>

      <Panel title="Invite user">
        <InviteForm onSubmit={(v) => invite.mutate(v)} disabled={invite.isPending} />
      </Panel>

      <Panel>
        <form
          className="inline filters"
          onSubmit={(e) => e.preventDefault()}
          style={{ gap: 12 }}
        >
          <input
            type="search"
            placeholder="Search email / name / HubSpot id"
            value={search.q ?? ""}
            onChange={(e) => navigate({ search: { ...search, q: e.target.value || undefined } })}
            style={{ minWidth: 260, flex: 1 }}
          />
          <select
            value={search.role ?? ""}
            onChange={(e) =>
              navigate({
                search: { ...search, role: oneOf(e.target.value, ["admin", "rep", "finance"] as const) },
              })
            }
          >
            <option value="">All roles</option>
            <option value="rep">rep</option>
            <option value="finance">finance</option>
            <option value="admin">admin</option>
          </select>
          <select
            value={search.active ?? ""}
            onChange={(e) =>
              navigate({ search: { ...search, active: oneOf(e.target.value, ["1", "0"] as const) } })
            }
          >
            <option value="">All status</option>
            <option value="1">Active</option>
            <option value="0">Inactive</option>
          </select>
          {Object.keys(search).length > 0 && (
            <button type="button" onClick={() => navigate({ search: {} })} className="muted" style={{ background: "transparent", border: "1px solid transparent" }}>
              Reset
            </button>
          )}
        </form>
      </Panel>

      <Panel padding="tight">
        {data.users.length === 0 ? (
          <EmptyState icon="generic" title="No users match" body="Try loosening the filters." />
        ) : (
          <DataTable>
            <table>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Name</th>
                  <th>Role</th>
                  <th>HubSpot owner</th>
                  <th>Salary ccy</th>
                  <th>Structure</th>
                  <th>Active</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.users.map((u) => {
                  const structure = structureByUser.get(u.id);
                  return (
                    <UserRowView
                      key={u.id}
                      user={u}
                      structure={structure}
                      onUpdate={(patch) => update.mutate({ id: u.id, ...patch })}
                      onDelete={async () => {
                        const ok = await confirm({
                          title: "Delete user",
                          prompt: `Delete ${u.email}? Only works if they have no commission rows.`,
                          tone: "destructive",
                          confirmLabel: "Delete",
                        });
                        if (ok) remove.mutate(u.id);
                      }}
                      onAnonymize={async () => {
                        const ok = await confirm({
                          title: "Anonymise user (GDPR)",
                          prompt: `Anonymise ${u.email}? Their PII is scrubbed and auth is banned, but the row id stays so commissions, paid_by, and deal ownership still join correctly.`,
                          tone: "destructive",
                          confirmLabel: "Anonymise",
                        });
                        if (ok) anonymize.mutate(u.id);
                      }}
                    />
                  );
                })}
              </tbody>
            </table>
          </DataTable>
        )}
      </Panel>
    </>
  );
}

function SyncOwnersForm({
  onSubmit,
  disabled,
}: {
  onSubmit: (v: { default_role: "admin" | "rep" | "finance"; mark_active: boolean }) => void;
  disabled: boolean;
}) {
  const [role, setRole] = useState<"admin" | "rep" | "finance">("rep");
  const [markActive, setMarkActive] = useState(false);
  const confirm = useConfirm();

  return (
    <form
      className="inline"
      style={{ gap: 12, marginTop: 12, alignItems: "center" }}
      onSubmit={async (e) => {
        e.preventDefault();
        const ok = await confirm({
          title: "Sync from HubSpot",
          prompt: "Pull all HubSpot owners and create profile rows for any new ones. Existing users are left alone. Safe to re-run, may take 10–30 seconds.",
          confirmLabel: "Sync",
        });
        if (ok) onSubmit({ default_role: role, mark_active: markActive });
      }}
    >
      <label className="muted">Default role</label>
      <select value={role} onChange={(e) => setRole(e.target.value as "admin" | "rep" | "finance")}>
        <option value="rep">rep</option>
        <option value="finance">finance</option>
        <option value="admin">admin</option>
      </select>
      <label style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
        <input type="checkbox" checked={markActive} onChange={(e) => setMarkActive(e.target.checked)} />
        <span>mark active (leave unchecked to create inactive)</span>
      </label>
      <button type="submit" className="primary" disabled={disabled}>
        {disabled ? "Syncing…" : "Sync from HubSpot"}
      </button>
    </form>
  );
}

function InviteForm({
  onSubmit,
  disabled,
}: {
  onSubmit: (v: {
    email: string;
    full_name: string | null;
    role: string;
    hubspot_owner_id: string | null;
    salary_currency: string | null;
  }) => void;
  disabled: boolean;
}) {
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [role, setRole] = useState("rep");
  const [hubspotOwnerId, setHubspotOwnerId] = useState("");
  const [salaryCcy, setSalaryCcy] = useState("");

  function submit(e: FormEvent) {
    e.preventDefault();
    onSubmit({
      email: email.trim().toLowerCase(),
      full_name: fullName.trim() || null,
      role,
      hubspot_owner_id: hubspotOwnerId.trim() || null,
      salary_currency: salaryCcy.trim().toUpperCase() || null,
    });
    setEmail("");
    setFullName("");
    setHubspotOwnerId("");
    setSalaryCcy("");
  }

  return (
    <form
      onSubmit={submit}
      style={{ display: "grid", gridTemplateColumns: "2fr 2fr 1fr 1.5fr 1fr auto", gap: 8, alignItems: "end" }}
    >
      <input required type="email" placeholder="email@bookboost.io" value={email} onChange={(e) => setEmail(e.target.value)} />
      <input placeholder="Full name" value={fullName} onChange={(e) => setFullName(e.target.value)} />
      <select value={role} onChange={(e) => setRole(e.target.value)} required>
        <option value="rep">rep</option>
        <option value="finance">finance</option>
        <option value="admin">admin</option>
      </select>
      <input placeholder="HubSpot owner ID" value={hubspotOwnerId} onChange={(e) => setHubspotOwnerId(e.target.value)} />
      <input
        placeholder="GBP"
        maxLength={3}
        pattern="[A-Za-z]{3}"
        value={salaryCcy}
        onChange={(e) => setSalaryCcy(e.target.value)}
        style={{ textTransform: "uppercase" }}
      />
      <button type="submit" className="primary" disabled={disabled}>
        Invite
      </button>
    </form>
  );
}

function UserRowView({
  user,
  structure,
  onUpdate,
  onDelete,
  onAnonymize,
}: {
  user: UserRow;
  structure: StructureRow | undefined;
  onUpdate: (patch: Partial<UserRow>) => void;
  onDelete: () => void;
  onAnonymize: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [role, setRole] = useState(user.role);
  const [hubspotOwnerId, setHubspotOwnerId] = useState(user.hubspot_owner_id ?? "");
  const [salaryCurrency, setSalaryCurrency] = useState(user.salary_currency ?? "");
  const [active, setActive] = useState(user.active);

  return (
    <tr style={!user.active ? { opacity: 0.55 } : undefined}>
      <td><code style={{ fontSize: 11 }}>{user.email}</code></td>
      <td>{user.full_name ?? "—"}</td>
      <td>
        {editing ? (
          <select
            value={role}
            onChange={(e) => {
              const next = oneOf(e.target.value, ["admin", "rep", "finance"] as const);
              if (next) setRole(next);
            }}
          >
            <option value="rep">rep</option>
            <option value="finance">finance</option>
            <option value="admin">admin</option>
          </select>
        ) : (
          <span className="badge">{user.role}</span>
        )}
      </td>
      <td className="muted" style={{ fontSize: 12 }}>
        {editing ? (
          <input value={hubspotOwnerId} onChange={(e) => setHubspotOwnerId(e.target.value)} style={{ width: 120 }} />
        ) : (
          user.hubspot_owner_id ?? "—"
        )}
      </td>
      <td>
        {editing ? (
          <input
            value={salaryCurrency}
            onChange={(e) => setSalaryCurrency(e.target.value)}
            maxLength={3}
            style={{ width: 60, textTransform: "uppercase" }}
          />
        ) : (
          user.salary_currency ?? "—"
        )}
      </td>
      <td className="muted" style={{ fontSize: 12 }}>
        {structure ? (
          <>
            {structure.structure_type} · {formatPercent(structure.base_commission_rate)}
          </>
        ) : (
          <span className="err">missing</span>
        )}
      </td>
      <td>
        {editing ? (
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
        ) : (
          <span className={user.active ? "ok" : "muted"}>{user.active ? "✓" : "—"}</span>
        )}
      </td>
      <td className="muted" style={{ fontSize: 12 }}>{formatDate(user.created_at)}</td>
      <td>
        <div style={{ display: "inline-flex", gap: 6 }}>
          {editing ? (
            <>
              <button
                className="primary"
                style={{ padding: "4px 10px", fontSize: 12 }}
                onClick={() => {
                  onUpdate({
                    role,
                    hubspot_owner_id: hubspotOwnerId.trim() || null,
                    salary_currency: salaryCurrency.trim().toUpperCase() || null,
                    active,
                  });
                  setEditing(false);
                }}
              >
                Save
              </button>
              <button
                style={{ padding: "4px 10px", fontSize: 12 }}
                onClick={() => {
                  setRole(user.role);
                  setHubspotOwnerId(user.hubspot_owner_id ?? "");
                  setSalaryCurrency(user.salary_currency ?? "");
                  setActive(user.active);
                  setEditing(false);
                }}
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              <button style={{ padding: "4px 10px", fontSize: 12 }} onClick={() => setEditing(true)}>
                Edit
              </button>
              {(user.role === "rep" || user.role === "finance") && (
                <ImpersonateButton
                  userId={user.id}
                  label={user.full_name ?? user.email}
                  style={{ padding: "4px 10px", fontSize: 12 }}
                />
              )}
              <Link to="/admin/reps/$id/structures" params={{ id: user.id }} style={{ padding: "4px 10px", fontSize: 12, border: "1px solid var(--gray300)", borderRadius: "var(--radius)", color: "var(--ink)", textDecoration: "none" }}>
                Structure
              </Link>
              <button
                style={{ padding: "4px 10px", fontSize: 12, color: "var(--warn)", borderColor: "var(--warn)" }}
                onClick={onAnonymize}
              >
                Anonymise
              </button>
              <button
                style={{ padding: "4px 10px", fontSize: 12, color: "var(--err)", borderColor: "var(--err)" }}
                onClick={onDelete}
              >
                Delete
              </button>
            </>
          )}
        </div>
      </td>
    </tr>
  );
}
